/**
 * Shared SignalR connection manager for the DocumentProcessingHub.
 * Both useDocumentHub and useWorkspaceHub share the same WebSocket connection.
 * The connection sends the access token for authentication.
 */
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel,
} from '@microsoft/signalr';
import { PublicClientApplication } from '@azure/msal-browser';
import { getRuntimeAccessToken, setRuntimeMsalInstance } from '@/auth/runtimeToken';
import { logger } from '@/lib/logger';

let sharedConnection: HubConnection | null = null;
let activeSubscribers = 0;
let connectionPromise: Promise<void> | null = null;
let disconnectTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Set the MSAL instance for SignalR token acquisition.
 * Must be called once during app initialization (e.g., in AppShell).
 */
export function setHubMsalInstance(instance: PublicClientApplication): void {
  setRuntimeMsalInstance(instance);
}

export function getOrCreateHubConnection(): HubConnection {
  if (!sharedConnection) {
    sharedConnection = new HubConnectionBuilder()
      .withUrl('/hubs/document-processing', {
        accessTokenFactory: async () => (await getRuntimeAccessToken()) ?? '',
        // LongPolling needs the token in the query string which withUrl handles automatically
        transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(LogLevel.Warning)
      .build();

    sharedConnection.onclose(() => {
      logger.debug('SignalR', 'Connection closed');
      connectionPromise = null;
    });

    sharedConnection.onreconnected(() => {
      logger.debug('SignalR', 'Reconnected');
    });
  }
  return sharedConnection;
}

export async function ensureHubConnected(
  connection: HubConnection,
): Promise<void> {
  if (connection.state === HubConnectionState.Connected) return;
  if (
    connection.state === HubConnectionState.Connecting ||
    connection.state === HubConnectionState.Reconnecting
  ) {
    if (connectionPromise) return connectionPromise;
  }

  connectionPromise = connection.start().then(() => {
    logger.debug('SignalR', 'Connected to DocumentProcessingHub');
  });
  return connectionPromise;
}

export function incrementSubscribers(): void {
  activeSubscribers++;
  // Cancel any pending disconnect — a new subscriber just joined
  if (disconnectTimer) {
    clearTimeout(disconnectTimer);
    disconnectTimer = null;
  }
}

export function decrementAndMaybeDisconnect(): void {
  activeSubscribers--;
  if (activeSubscribers <= 0 && sharedConnection) {
    // Delay disconnect to avoid race conditions during React render cycles
    // (e.g. unmount + remount of components using the same connection)
    disconnectTimer = setTimeout(() => {
      if (activeSubscribers <= 0 && sharedConnection) {
        sharedConnection.stop().catch(() => {});
        sharedConnection = null;
        connectionPromise = null;
        activeSubscribers = 0;
      }
      disconnectTimer = null;
    }, 500);
  }
}

export function getSharedConnection(): HubConnection | null {
  return sharedConnection;
}
